/**
 * The small mono label above a figure, a panel section or a diagram node —
 * "MATCHES NOW", "BY SOURCE", "AVAILABLE TODAY".
 *
 * ## WHY ONE COMPONENT FOR FOUR CONCEPT CLASSES
 *
 * The Blueprint concept drew this label four times under four names
 * (`.sb-k`, `.bp-k`, `.bp-node-k`, `.sb-res-sec`), at three letter-spacings
 * and three sizes that differ by a hundredth of a rem. Nobody can see that
 * difference; everybody pays for it the day one of them is retuned and the
 * other three are not. So the role is defined once, as a fixed class string,
 * and the only things a caller may choose are the ones that carry meaning:
 * whether the label is Wend's own (`brand`) or plain (`muted`), whether it
 * trails a hairline to the edge (`rule`), and which element it is.
 *
 * ## THE COLOUR RULES IT KEEPS
 *
 * Every concept kicker was drawn in the faint token. That token is not a text
 * colour in this product (theme.css, slice 25: 3.60:1 on surface-1, and small
 * mono caps are exactly the text that needs the full 4.5:1). So the default is
 * `text-muted`, and differentiation from body text is carried by the mono
 * face, the caps and the tracking — never by dimming it below legibility.
 * `brand` is `text-brand-ink`, not `brand-b`: brand-b measures 4.47:1 on
 * surface-2, where most of these labels sit.
 *
 * ## WHY 0.13em AND NOT THE PAGE EYEBROW'S 0.16em
 *
 * `PageHeader`'s eyebrow is tracked 0.16em, and `verify-sense-screens` uses
 * that tracking to count page eyebrows on a screen. A kicker inside a panel is
 * a different thing — a label, not "you are in Sense" — and if it shared the
 * tracking, every panel would read to that suite as a second page header.
 *
 * ## THE HOLE IT LEAVES
 *
 * It does not choose the heading level for you. When a kicker names a section
 * a screen reader should be able to jump to, pass `as="h3"`; the default is a
 * `p`, because most kickers label a figure rather than open a section, and an
 * outline full of "MATCHES NOW" headings is worse than none.
 */
import type { HTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/** The kicker type role. Exported so a node or a log can match it without the element. */
export const KICKER_CLASS = 'font-mono text-[0.64rem] font-medium uppercase leading-normal tracking-[0.13em]';

const KICKER_TONE = {
  muted: 'text-muted',
  brand: 'text-brand-ink',
} as const;

export type KickerTone = keyof typeof KICKER_TONE;

export interface KickerProps extends HTMLAttributes<HTMLElement> {
  /** Default `'muted'`. `brand` is for a label that names a part of Wend. */
  tone?: KickerTone;
  /** Draw a hairline from the end of the text to the edge, as a section rule. */
  rule?: boolean;
  /**
   * Default `'p'`. Use `'h3'` when the kicker opens a section, `'h2'` when it
   * heads a panel that stands alone on its screen.
   */
  as?: 'p' | 'span' | 'dt' | 'h2' | 'h3';
}

export function Kicker({ tone = 'muted', rule = false, as: Tag = 'p', className, ...rest }: KickerProps) {
  return (
    <Tag
      className={cn(
        KICKER_CLASS,
        KICKER_TONE[tone],
        rule ? 'flex items-center gap-2 after:h-px after:flex-1 after:bg-line' : 'block',
        className,
      )}
      {...rest}
    />
  );
}
